import axios from 'axios';
import { KOUNT_DECISION_ENDPOINT, CAR_FINANCE_BACKEND_URL, IS_TEST_ENVIRONMENT, DEFAULT_KOUNT_FALLBACK_MOBILE } from '../config/env';
import packageJson from '../../package.json';
import { collectKountData } from './kount';

const trimToUndefined = (value) => {
  if (value === null || value === undefined) {
    return undefined;
  }
  const trimmed = `${value}`.trim();
  return trimmed || undefined;
};

// Some email clients fail to decode quoted-printable MIME encoding on the deeplink,
// leaking the literal '=3D' (the escaped form of '=') into the query string in place of
// a real '=' delimiter — e.g. "?uniqueID=HPER369V" arrives as "?uniqueID=3DHPER369V".
// urlencode() always percent-encodes a literal '=' inside a value as '%3D' (with a
// percent sign), so a bare '=3D' can only ever be a corrupted delimiter, never legitimate
// value content — safe to reverse unconditionally, for every param, before parsing.
const deQuotedPrintable = (search) => search.replace(/=3D/gi, '=');

const getUrlParam = (paramName) => {
  const urlParams = new URLSearchParams(deQuotedPrintable(window.location.search));
  const key = Array.from(urlParams.keys()).find(
    (k) => k.toLowerCase() === paramName.toLowerCase()
  );
  return key ? urlParams.get(key) : null;
};

const omitEmpty = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );

const normaliseDob = (value) => {
  if (!value) return undefined;
  const trimmed = trimToUndefined(value);
  if (!trimmed) return undefined;
  // Expect DD/MM/YYYY format, convert to YYYY-MM-DD
  const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return undefined;
  const [, day, month, year] = match;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

const sanitisePhone = (value) => {
  if (!value) return undefined;
  const trimmed = trimToUndefined(value);
  if (!trimmed) return undefined;
  // Strip all whitespace
  return trimmed.replace(/\s+/g, '');
};

const resolveMobileDetails = (mobileNumber) => {
  const sanitised = sanitisePhone(mobileNumber);
  if (sanitised) {
    return {
      mobileNumber: sanitised,
      mobileProvided: true,
      mobileSource: 'url_param',
    };
  }
  return {
    mobileNumber: DEFAULT_KOUNT_FALLBACK_MOBILE,
    mobileProvided: false,
    mobileSource: 'fallback',
  };
};

const buildAddressFromParams = () => {
  const address = {
    line1: trimToUndefined(getUrlParam('currentAddressLine1')) || trimToUndefined(getUrlParam('line1')),
    line2: trimToUndefined(getUrlParam('currentAddressLine2')) || trimToUndefined(getUrlParam('line2')),
    city: trimToUndefined(getUrlParam('currentAddressCity')) || trimToUndefined(getUrlParam('city')),
    postcode: trimToUndefined(getUrlParam('currentAddressPostcode')) || trimToUndefined(getUrlParam('postcode')),
  };

  // Return undefined if no address fields are present
  if (!address.line1 && !address.line2 && !address.city && !address.postcode) {
    return undefined;
  }

  return address;
};

const buildSubmissionPayload = ({
  uniqueID,
  clientid,
  kountData,
  ipData,
  brand,
  datastoreWebFormId,
  kountPrefill,
  termsAccepted = true,
}) => {
  return {
    web_form_id: datastoreWebFormId,
    uniqueID,
    termsAccepted,
    websiteAquisitionIpAddress: ipData?.ip || null,
    testing: IS_TEST_ENVIRONMENT,
    marketingAcquisitionBrand: brand.name,
    title: kountPrefill?.title,
    firstName: kountPrefill?.forename,
    surname: kountPrefill?.surname,
    dob: kountPrefill?.dateOfBirth,
    email: kountPrefill?.email,
    mobile: kountPrefill?.mobileNumber,
    currentAddress: kountPrefill?.currentAddress,
    kountRequest: omitEmpty({
      sessionId: kountData.kountSessionId,
      clientReference: uniqueID || clientid || kountData.kountSessionId,
    }),
    kountResponse: {
      kountSessionId: kountData.kountSessionId,
      kountClientId: kountData.kountClientId,
      kountEnvironment: kountData.kountEnvironment,
      kountIsSinglePageApp: kountData.kountIsSinglePageApp,
      kountCollectedAt: kountData.kountCollectedAt,
      kountSdkInvoked: kountData.kountSdkInvoked,
      kountDeviceData: kountData.kountDeviceData || null,
      kountDecisionToken: kountData.kountDecisionToken || null,
      kountCollectionError: kountData.kountCollectionError || null,
      kountDecision: null,
      kountTransactionId: null,
      kountStatus: null,
      kountDecisionedAt: null,
    },
  };
};

const buildKountPayload = ({
  uniqueID,
  clientid,
  kountData,
  kountPrefill,
}) => {
  return {
    sessionId: kountData.kountSessionId,
    clientReference: uniqueID || clientid || kountData.kountSessionId,
    title: kountPrefill?.title || '',
    forename: kountPrefill?.forename || '',
    surname: kountPrefill?.surname || '',
    dateOfBirth: kountPrefill?.dateOfBirth,
    email: kountPrefill?.email || '',
    mobileNumber: kountPrefill?.mobileNumber,
    currentAddress: kountPrefill?.currentAddress,
  };
};

const extractKountDecision = (kountResponse) => {
  const decision = `${kountResponse?.decision || ''}`.trim().toUpperCase();
  const status = `${kountResponse?.status || ''}`.trim().toUpperCase();
  const raw = kountResponse?.raw?.result?.jsonReport?.identityResponse || null;

  return {
    kountDecision: decision || null,
    kountTransactionId: kountResponse?.transactionId || null,
    kountStatus: status || null,
    kountDecisionedAt: new Date().toISOString(),
    kountInquiryId: raw?.inquiryId || null,
    kountDeviceSessionId: raw?.deviceSessionId || null,
    kountDeviceId: raw?.deviceId || null,
    kountCorrelationId: raw?.correlationId || null,
    kountSegment: raw?.policyManagement?.segmentExecuted?.name || null,
    kountTags: raw?.policyManagement?.tags || null,
  };
};

const submitKountDecision = async (identityPayload, endpoint = KOUNT_DECISION_ENDPOINT) => {
  const response = await axios.post(endpoint, identityPayload, {
    headers: { 'Content-Type': 'application/json' },
  });

  return response.data?.response || response.data;
};

const submitToDatastore = async (payload, ipData) => {
  const datastoreSubmissionPayload = {
    ...payload,
    websiteVersion: packageJson.version,
    submittedAt: new Date().toISOString(),
  };

  if (payload.termsAccepted === true) {
    datastoreSubmissionPayload.termsAcceptedAt = new Date().toISOString();
  }

  const datastoreUrl = ipData?.dataid
    ? `${CAR_FINANCE_BACKEND_URL}/api/1.0/datastore/${ipData.dataid}`
    : `${CAR_FINANCE_BACKEND_URL}/api/1.0/datastore`;
  const datastoreMethod = ipData?.dataid ? 'put' : 'post';
  const res = await axios[datastoreMethod](datastoreUrl, datastoreSubmissionPayload, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': process.env.REACT_APP_DATASTORE_API_KEY,
    },
  });

  return res;
};

export async function submitMotJourney({
  uniqueID,
  clientid,
  brand,
  datastoreWebFormId,
  kountPrefill,
  ipData,
  termsAccepted = true,
  additionalPayload = {},
}) {
  const kd = await collectKountData(kountPrefill?.email || "");

  let payload = {
    ...additionalPayload,
    ...buildSubmissionPayload({
      uniqueID,
      clientid,
      kountData: kd,
      ipData,
      brand,
      datastoreWebFormId,
      kountPrefill,
      termsAccepted,
    }),
  };

  const kountDecisionPayload = buildKountPayload({
    uniqueID,
    clientid,
    kountData: kd,
    kountPrefill,
  });

  let kountError = "";

  try {
    const kountDecisionResponse = await submitKountDecision(kountDecisionPayload);

    payload = {
      ...payload,
      kountRequest: kountDecisionPayload,
      kountResponse: {
        ...payload.kountResponse,
        ...extractKountDecision(kountDecisionResponse),
      },
    };
  } catch (kountDecisionError) {
    kountError =
      kountDecisionError?.response?.data?.detail ||
      kountDecisionError?.message ||
      "We couldn't complete the KOUNT verification step right now.";

    payload = {
      ...payload,
      kountRequest: kountDecisionPayload,
      kountResponse: {
        ...payload.kountResponse,
        kountDecision: null,
        kountTransactionId: null,
        kountStatus: "FAILED",
        kountDecisionedAt: new Date().toISOString(),
        kountError,
      },
    };
  }

  const response = await submitToDatastore(payload, ipData);

  return { response, kountError };
}


export {
  trimToUndefined,
  deQuotedPrintable,
  getUrlParam,
  normaliseDob,
  sanitisePhone,
  resolveMobileDetails,
  buildAddressFromParams,
  omitEmpty,
  buildSubmissionPayload,
  buildKountPayload,
  extractKountDecision,
  submitKountDecision,
  submitToDatastore,
};
